import { useState, useEffect } from 'react';
import type { Card } from '@mtg-deck-tool/shared';
import { api } from '../lib/api-client.js';

export interface UseCardTagsResult {
  tags: string[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches oracle tags for a single card using the existing profile endpoint.
 * Returns a flat sorted array of tag display names.
 * On error, the component should show "Tags unavailable".
 *
 * @param card - The card to fetch tags for, or null to disable the query.
 * @returns Object with tags (sorted display names), isLoading, and isError state.
 */
export function useCardTags(card: Card | null): UseCardTagsResult {
  const [tags, setTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!card) return;

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    api
      .profileDeck([card.name])
      .then((response) => {
        if (!cancelled) {
          const sorted = response.profile.entries
            .map((e) => e.tag.displayName)
            .sort((a, b) => a.localeCompare(b));
          setTags(sorted);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTags([]);
          setIsError(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [card?.oracleId]);

  return { tags, isLoading, isError };
}
